import { numByteSize } from "./NumType";
import { isByteOffset } from "../data/formats/3d_tiles/table";
/**
 * Internal function that normalizes the single number representations valid in an on-disk FeatureTable into a number.
 *
 * Will return numbers unchanged.
 *
 * Destructures arrays by only returning the first element.
 *
 * Will read a number of numType from the view if a ByteOffset.
 * @param view
 * @param numType
 * @param v
 */
export function readSingleNum(view, numType, v) {
    if (typeof v === "undefined" || typeof v === "number") {
        return v;
    }
    else if (isByteOffset(v)) {
        return readNumFromBlob(view, numType, v.byteOffset);
    }
    else {
        return v[0];
    }
}
/**
 * Internal function that normalizes the various array representations valid in an on-disk FeatureTable into an array.
 *
 * Promotes single numbers to an array of size 1.
 *
 * If an array, returns it verbatim if it matches the required length. Otherwise, returns undefined.
 *
 * If ByteOffset, reads the length numbers of numType from the data view and returns them in an array.
 * @param view
 * @param length
 * @param numType
 * @param v
 */
export function readMultiNum(view, length, numType, v) {
    if (typeof v === "undefined") {
        // If we got undefined return it verbatim
        return v;
    }
    else if (typeof v === "number") {
        // If we got a single number wrap it in an array
        return [v];
    }
    else if (isByteOffset(v)) {
        // If we got a data reference read the data from the blob, assuming the data is stored as the provided NumType
        const array = [];
        const byteSize = numByteSize(numType);
        for (let i = 0; i < length; i += 1) {
            const offset = i * byteSize;
            const num = readNumFromBlob(view, numType, v.byteOffset + offset);
            array.push(num);
        }
        return array;
    }
    else {
        if (v.length !== length)
            return undefined;
        return v;
    }
}
/**
 * Internal function that switches the get* function used to load from the DataView based on the numType variant.
 * @param view
 * @param numType
 * @param byteOffset
 */
export function readNumFromBlob(view, numType, byteOffset) {
    switch (numType) {
        case 3 /* NumType.U64 */:
            throw new Error("U64 can't be represented in a double precision floating point (number)");
        case 7 /* NumType.I64 */:
            throw new Error("I64 can't be represented in a double precision floating point (number)");
        case 0 /* NumType.U8 */:
            return view.getUint8(byteOffset);
        case 4 /* NumType.I8 */:
            return view.getInt8(byteOffset);
        case 1 /* NumType.U16 */:
            return view.getUint16(byteOffset, true);
        case 5 /* NumType.I16 */:
            return view.getInt16(byteOffset, true);
        case 2 /* NumType.U32 */:
            return view.getUint32(byteOffset, true);
        case 6 /* NumType.I32 */:
            return view.getInt32(byteOffset, true);
        case 8 /* NumType.F32 */:
            return view.getFloat32(byteOffset, true);
        case 9 /* NumType.F64 */:
            return view.getFloat64(byteOffset, true);
    }
}
export function readFeatureTable(view, headerSize = 28) {
    // Read feature table sizes from file header
    const jsonLength = view.getUint32(12, true);
    const binLength = view.getUint32(16, true);
    // Extract the feature table JSON
    const decoder = new TextDecoder("utf-8");
    const textView = new DataView(view.buffer, view.byteOffset + headerSize, jsonLength);
    const text = decoder.decode(textView);
    // Create a slice to the feature table binary data
    const data = new DataView(view.buffer, view.byteOffset + headerSize + jsonLength, binLength);
    return {
        text,
        data,
    };
}
export function readBatchTable(view, headerSize = 28) {
    // Grab the size of the feature table so we know where the batch table starts
    const tableOffset = view.getUint32(12, true) + view.getUint32(16, true);
    // Read feature table sizes from file header
    const jsonLength = view.getUint32(20, true);
    const binLength = view.getUint32(24, true);
    // Check if the batch table is present
    if (jsonLength === 0)
        return undefined;
    // Extract the feature table JSON
    const decoder = new TextDecoder("utf-8");
    const textView = new DataView(view.buffer, view.byteOffset + headerSize + tableOffset, jsonLength);
    const text = decoder.decode(textView);
    // Create a slice to the feature table binary data
    const data = new DataView(view.buffer, view.byteOffset + headerSize + jsonLength + tableOffset, binLength);
    return {
        text,
        data,
    };
}
